import { useEffect, useState } from 'react';
import { fetchNearby } from '../lib/api';
import { modeShorts } from '../lib/palette';
import type { NearbyStop, Point } from '../lib/types';

interface RowProps {
  point: Point;
  index: number;
  share: number;
  exploring: boolean;
  onLabel: (id: string, label: string) => void;
  onWeight: (id: string, weight: number) => void;
  onRemove: (id: string) => void;
  onExplore: (id: string) => void;
  onFocus: (id: string) => void;
}

function NearestStop({ lat, lon }: { lat: number; lon: number }) {
  const [stop, setStop] = useState<NearbyStop | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setStop(null);
    fetchNearby(lat, lon, controller.signal)
      .then((list) => setStop(list[0] ?? null))
      .catch(() => setStop(null));
    return () => controller.abort();
  }, [lat, lon]);

  if (!stop) return <span className="point__stop point__stop--empty">no stop within walking distance</span>;
  return (
    <span className="point__stop">
      {modeShorts(stop.modes).map((m) => (
        <span key={m.short} className={`badge badge--${m.short}`} title={m.name}>
          {m.short}
        </span>
      ))}
      {stop.name} · {Math.round(stop.walk / 60)} min walk
    </span>
  );
}

function PointRow({
  point,
  index,
  share,
  exploring,
  onLabel,
  onWeight,
  onRemove,
  onExplore,
  onFocus,
}: RowProps) {
  return (
    <li className={`point${exploring ? ' point--exploring' : ''}`}>
      <div className="point__head">
        <button
          type="button"
          className="point__index"
          title="Centre the map here"
          onClick={() => onFocus(point.id)}
        >
          {index + 1}
        </button>
        <input
          className="point__label"
          value={point.label}
          placeholder={point.resolving ? 'Looking up address…' : 'Name this place'}
          aria-label={`Label for place ${index + 1}`}
          onChange={(e) => onLabel(point.id, e.target.value)}
        />
        <button
          type="button"
          className={`point__btn${exploring ? ' is-on' : ''}`}
          title={exploring ? 'Back to the combined view' : 'Show travel times from here to everywhere'}
          aria-pressed={exploring}
          onClick={() => onExplore(point.id)}
        >
          {exploring ? 'Combined' : 'From here'}
        </button>
        <button
          type="button"
          className="point__btn point__btn--remove"
          title="Remove this place"
          aria-label={`Remove place ${index + 1}`}
          onClick={() => onRemove(point.id)}
        >
          ×
        </button>
      </div>
      <div className="point__meta">
        <NearestStop lat={point.lat} lon={point.lon} />
      </div>
      <div className="point__weight">
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={point.weight}
          aria-label={`Importance of ${point.label || `place ${index + 1}`}`}
          onChange={(e) => onWeight(point.id, Number(e.target.value))}
        />
        <span className="point__share" title="Share of the weighted average">
          {Math.round(share * 100)}%
        </span>
      </div>
    </li>
  );
}

interface Props {
  points: Point[];
  exploringId: string | null;
  onLabel: (id: string, label: string) => void;
  onWeight: (id: string, weight: number) => void;
  onRemove: (id: string) => void;
  onExplore: (id: string) => void;
  onFocus: (id: string) => void;
}

export function PointList({ points, exploringId, ...handlers }: Props) {
  const total = points.reduce((sum, p) => sum + p.weight, 0) || 1;
  return (
    <ol className="points">
      {points.map((p, i) => (
        <PointRow
          key={p.id}
          point={p}
          index={i}
          share={p.weight / total}
          exploring={p.id === exploringId}
          {...handlers}
        />
      ))}
    </ol>
  );
}
